import { useState } from "react";
import Link from "next/link";
import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { Button } from "@aqar-app/aqar-design-system";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  GET_EXPIRED_LISTING,
  GET_OWNER_LISTINGS,
} from "../../graphql/queries/user";
import { useAuthState } from "../../contexts/auth/auth.provider";
import classes from "./userListings.module.scss";
import config from "../../common/config";
import logger from "../../../console";
import Skeleton from "../../components/skeleton";
import { Listing } from "../../components/Listing/ListingCard";
import { FIND_LISTINGS } from "../../graphql/queries/find.listings";
import { useRouter } from "next/router";
import { usePath } from "@/contexts/path/path";
import OwnerListingCard from "@/components/Listing/ListingCard/owner";
import { trackMonthlyAdDraftClick } from "@/lib/tracking/trackMonthlyAd";
import { getMaintainedParam } from "@/lib/apolloClient";
import { trackMapAuctionListingClicked } from "@/lib/tracking/trackAuctionListingEvents";

const findDefaultVars = {
  size: config.LISTINGS_PAGE_SIZE,
  from: 0,
  sort: config.LISTINGS_DEFAULT_SORTER,
};

export function ListingHolder() {
  return (
    <Skeleton className={classes.Holder}>
      <div>
        <div>
          <Skeleton.Text style={{ width: "8rem", height: ".7rem" }} />
        </div>
        <Skeleton.Text style={{ width: "12rem", height: ".9rem" }} />
        <Skeleton.Text style={{ width: "20rem", height: ".7rem" }} />
      </div>
      <Skeleton.Item />
    </Skeleton>
  );
}

function Loader() {
  return (
    <>
      <ListingHolder />
      <ListingHolder />
      <ListingHolder />
      <ListingHolder />
      <ListingHolder />
    </>
  );
}

function UserListings({
  expired,
  drafts,
  isOwner,
  userId,
  selectRoute,
  userListings,
  userListingsTotal,
}) {
  const { token, user } = useAuthState();
  const [listings, setListings] = useState(userListings || []);
  const [total, setTotal] = useState(userListingsTotal || 0);
  const [infinite, setInfinite] = useState(false);
  const router = useRouter();
  const { updateContext, categoriesDictionary, previousPath } = usePath();
  const [fetching, setFetching] = useState(!userListings.length);

  const maintainedParam = getMaintainedParam(user?.cooperative);

  const { fetchMore: fetchMoreOwnerListings, loading } = useQuery(
    GET_OWNER_LISTINGS,
    {
      skip: !isOwner || expired,
      context: { token },
      variables: {
        draft: drafts,
      },
      onCompleted: ({ Listing: listing }) => {
        const newListings = listing?.getMyListings?.listings || [];
        const listingsTotal = listing?.getMyListings?.total ?? 0;
        setListings(newListings);
        setTotal(listingsTotal);
        setFetching(false);
      },
      onError: () => {
        setFetching(false);
      },
    },
  );

  const { fetchMore: fetchMoreUserListings } = useQuery(FIND_LISTINGS, {
    skip: isOwner || expired || !userId || listings.length,
    fetchPolicy: "no-cache",
    variables: {
      ...findDefaultVars,
      where: { user_id: { eq: userId }, ...maintainedParam },
    },
    onCompleted: ({ Web }) => {
      const newListings = Web?.find?.listings || [];
      const listingsTotal = Web?.find?.total || 0;
      setListings(newListings);
      setTotal(listingsTotal);
      setFetching(false);
    },
    onError: () => {
      setFetching(false);
    },
  });

  const { fetchMore: fetchMoreExpiredListings } = useQuery(
    GET_EXPIRED_LISTING,
    {
      skip: !expired || !isOwner,
      context: { token },
      fetchPolicy: "no-cache",
      onCompleted: ({ User }) => {
        const newListings = User?.expiredListings?.listings || [];
        const listingsTotal = User?.expiredListings?.total || 0;
        setListings(newListings);
        setTotal(listingsTotal);
        setFetching(false);
      },
      onError: () => {
        setFetching(false);
      },
    },
  );

  const fetchMoreData = () => {
    logger.log("Fetch More");
    if (isOwner) {
      if (expired) {
        setFetching(true);
        fetchMoreExpiredListings({
          variables: {
            offset: listings.length,
          },
        })
          .then(({ data }) => {
            const newEntries = data.User?.expiredListings?.listings;
            setListings((currentListings) => [
              ...currentListings,
              ...newEntries,
            ]);
          })
          .finally(() => setFetching(false));
      } else {
        setFetching(true);
        fetchMoreOwnerListings({
          variables: {
            offset: listings.length,
          },
        })
          .then(({ data }) => {
            const newEntries = data.Listing?.getMyListings?.listings;
            setListings((currentListings) => [
              ...currentListings,
              ...newEntries,
            ]);
          })
          .finally(() => setFetching(false));
      }
    } else {
      setFetching(true);

      fetchMoreUserListings({
        variables: {
          ...findDefaultVars,
          from: listings.length,
          where: { user_id: { eq: userId }, ...maintainedParam },
        },
      })
        .then(({ data }) => {
          const newEntries = data.Web?.find?.listings;
          setListings((currentListings) => [...currentListings, ...newEntries]);
        })
        .finally(() => setFetching(false));
    }
  };

  if ((fetching && !listings.length) || loading)
    return (
      <div>
        <ListingHolder />
        <ListingHolder />
        <ListingHolder />
        <ListingHolder />
        <ListingHolder />
        <ListingHolder />
        <ListingHolder />
        <ListingHolder />
      </div>
    );
  if (!listings.length)
    return (
      <div className={classes.emptyListingsNote}>
        {selectRoute ? (
          <p>
            لا يوجد لديك إعلانات حاليا. يمكنك إضافة إعلان من{" "}
            <Link className={classes.link} href="/add-listing" passHref>
              هنا
            </Link>
          </p>
        ) : (
          `لا يوجد إعلانات ${expired ? "منتهية." : "."}`
        )}
      </div>
    );
  if (!infinite)
    return (
      <div className={classes.listingsContainer}>
        {listings?.map((listing, index) =>
          isOwner ? (
            <OwnerListingCard
              key={listing.id}
              onClick={() => {
                if (isOwner && drafts) {
                  trackMonthlyAdDraftClick({
                    source: previousPath || decodeURI(document.referrer),
                  });
                }
                if (!selectRoute && isOwner) {
                  updateContext({
                    myListing: {
                      ...listing,
                      user,
                      user_id: user.user_id,
                    },
                    skipRouting: true,
                    listing: undefined,
                  });
                  return router.push(`/ad/${listing.id}`);
                }
                if (!selectRoute && !isOwner)
                  return router.push(`/ad/${listing.id}`);
                router.push(selectRoute + `?listing_id=${listing.id}`);
              }}
              index={index}
              listing={{ ...listing, path: `/ad/${listing.id}` }}
            />
          ) : (
            <Listing
              key={listing.id}
              onClick={() => {
                if (listing.is_auction)
                  trackMapAuctionListingClicked({
                    source: previousPath || decodeURI(document?.referrer),
                    propertyCategory:
                      categoriesDictionary?.[listing?.category]
                        ?.ga_property_category,
                    listingType:
                      categoriesDictionary?.[listing?.category]
                        ?.ga_listing_type,
                  });
                if (!selectRoute) return router.push(`/ad/${listing.id}`);
                router.push(selectRoute + `?listing_id=${listing.id}`);
              }}
              index={index}
              listing={{ ...listing, path: `/ad/${listing.id}` }}
            />
          ),
        )}

        {total > findDefaultVars.size && (
          <Button
            onClick={() => {
              setInfinite(true);
              fetchMoreData();
            }}
          >
            عرض المزيد
          </Button>
        )}
      </div>
    );
  return (
    <div className={classes.listingsContainer}>
      <InfiniteScroll
        scrollThreshold={
          (document.body.clientWidth > 800
            ? document.body.clientHeight - 700
            : document.body.clientHeight - 1200) /* Footer Height */ /
          document.body.clientHeight
        }
        dataLength={listings.length}
        next={fetchMoreData}
        hasMore={listings.length < total}
        scrollableTarget={document}
        loader={<></>}
        endMessage={
          loading ? null : (
            <div className={classes.End}>
              <span />
              <p>لا يوجد مزيد من الاعلانات</p>
            </div>
          )
        }
      >
        {listings?.map((listing, index) =>
          isOwner ? (
            <OwnerListingCard
              key={listing.id}
              onClick={() => {
                updateContext({
                  myListing: {
                    ...listing,
                    user,
                    user_id: user.user_id,
                  },
                  skipRouting: true,
                  listing: undefined,
                });
                if (!selectRoute && isOwner) {
                  updateContext({
                    myListing: {
                      ...listing,
                      user,
                      user_id: user.user_id,
                    },
                    skipRouting: true,
                    listing: undefined,
                  });
                  return router.push(`/ad/${listing.id}`);
                }
                if (!selectRoute && !isOwner)
                  return router.push(`/ad/${listing.id}`);
                router.push(selectRoute + `?listing_id=${listing.id}`);
              }}
              index={index}
              listing={{ path: selectRoute || `/ad/${listing.id}`, ...listing }}
            />
          ) : (
            <Listing
              key={listing.id}
              onClick={() => {
                if (listing.is_auction)
                  trackMapAuctionListingClicked({
                    source: previousPath || decodeURI(document?.referrer),
                    propertyCategory:
                      categoriesDictionary?.[listing?.category]
                        ?.ga_property_category,
                    listingType:
                      categoriesDictionary?.[listing?.category]
                        ?.ga_listing_type,
                  });
                updateContext({
                  skipRouting: true,
                  listing: undefined,
                });

                if (!selectRoute) return router.push(`/ad/${listing.id}`);
                router.push(selectRoute + `?listing_id=${listing.id}`);
              }}
              index={index}
              listing={{ path: selectRoute || `/ad/${listing.id}`, ...listing }}
            />
          ),
        )}
      </InfiniteScroll>
      {fetching && <Loader />}
    </div>
  );
}

UserListings.propTypes = {
  expired: PropTypes.bool,
  isOwner: PropTypes.bool,
  userId: PropTypes.number,
  selectRoute: PropTypes.string,
  userListings: PropTypes.arrayOf(PropTypes.any),
  userListingsTotal: PropTypes.number,
};

UserListings.defaultProps = {
  expired: false,
  isOwner: false,
  userId: undefined,
  selectRoute: undefined,
  userListings: [],
  userListingsTotal: 0,
};

export default UserListings;
